import { IWorkflowInput } from "@/interfaces/workflow.interface";
import { IWorkflowKeyWordRule } from "@/types/workflow-keywords.types";
import {
  finalizeWorkflowKeyWordRules,
  parseWorkflowKeyWords,
  serializeWorkflowKeyWords,
} from "@/utils/workflow-keywords";
import { readWorkflowKeyWords } from "@/utils/workflow-update-payload";

export const buildWorkflowSettingsInitialData = (
  workflow?: IWorkflowInput | null
): IWorkflowInput & { enableExpiration?: boolean; enableReactivation?: boolean } => {
  const workflowAny = workflow as any;

  // Expiration data
  const timeoutValue = workflowAny?.ConversationTimeout ?? workflowAny?.conversationTimeout;
  const hasTimeoutDefined = typeof timeoutValue === "number";
  const initialTimeout = hasTimeoutDefined ? (timeoutValue ?? 0) : 180;

  const expirationStepNameValue = workflowAny?.ExpirationStepName ?? workflowAny?.expirationStepName;
  const expirationStepName = expirationStepNameValue ?? null;

  const hasCustomExpiration = expirationStepName !== null && expirationStepName !== "";

  // Reactivation data
  const reactivationTimeoutValue = workflowAny?.reactivationTimeout ?? workflowAny?.ReactivationTimeout;
  const hasReactivationTimeout = reactivationTimeoutValue !== undefined && reactivationTimeoutValue !== null;
  const reactivationTimeout = hasReactivationTimeout ? (reactivationTimeoutValue ?? null) : null;

  const reactivationMessageValue = workflowAny?.reactivationMessage ?? workflowAny?.ReactivationMessage;
  const reactivationMessage = reactivationMessageValue ?? null;

  const reactivationMaxMessagesValue = workflowAny?.reactivationMaxMessages ?? workflowAny?.ReactivationMaxMessages;
  const reactivationMaxMessages = reactivationMaxMessagesValue ?? null;

  const reactivationIntervalValue = workflowAny?.reactivationInterval ?? workflowAny?.ReactivationInterval;
  const reactivationInterval = reactivationIntervalValue ?? null;

  const isReactivationEnabled =
    reactivationTimeout !== null && reactivationTimeout !== undefined && Number(reactivationTimeout) > 0;

  const productDisplayTypeRaw =
    workflowAny?.productDisplayType ?? workflowAny?.ProductDisplayType;
  const productDisplayType = String(productDisplayTypeRaw || "LIST").toUpperCase();

  const orderReportLayoutRaw = workflowAny?.orderReportLayout ?? workflowAny?.OrderReportLayout;
  const orderReportLayout = String(orderReportLayoutRaw ?? "").toUpperCase() === "R" ? "R" : "A";

  const productSearchAssertivenessRaw =
    workflowAny?.productSearchAssertiveness ?? workflowAny?.ProductSearchAssertiveness;
  const productSearchAssertiveness =
    productSearchAssertivenessRaw === null || productSearchAssertivenessRaw === undefined
      ? 50
      : Number(productSearchAssertivenessRaw);

  const workflowKeyWordsRaw =
    workflowAny?.workflowKeyWords ?? workflowAny?.WorkflowKeyWords;
  const workflowKeyWordsRules = parseWorkflowKeyWords(
    typeof workflowKeyWordsRaw === "string" ? workflowKeyWordsRaw : null
  );

  return {
    ...workflow,
    productDisplayType,
    orderReportLayout,
    productSearchAssertiveness: Number.isFinite(productSearchAssertiveness)
      ? productSearchAssertiveness
      : 50,
    ConversationTimeout: initialTimeout,
    ExpirationStepName: expirationStepName,
    enableExpiration: hasCustomExpiration,
    reactivationTimeout: reactivationTimeout,
    reactivationMessage: reactivationMessage,
    reactivationMaxMessages: reactivationMaxMessages,
    reactivationInterval: reactivationInterval,
    enableReactivation: isReactivationEnabled,
    workflowKeyWordsRules,
    workflowKeyWordDrafts: {},
  } as IWorkflowInput & {
    enableExpiration?: boolean;
    enableReactivation?: boolean;
    workflowKeyWordsRules?: IWorkflowKeyWordRule[];
    workflowKeyWordDrafts?: Record<number, string>;
  };
};

export const normalizeWorkflowSettingsData = (
  formData: IWorkflowInput & {
    enableExpiration?: boolean;
    enableReactivation?: boolean;
    workflowKeyWordsRules?: IWorkflowKeyWordRule[];
    workflowKeyWordDrafts?: Record<number, string>;
  },
  originalWorkflow?: IWorkflowInput | null
): IWorkflowInput => {
  const normalizeExpirationTimeout = (rawValue: unknown) => {
    if (rawValue === "" || rawValue === undefined || rawValue === null) {
      return 0;
    }

    const timeoutNum = Number(rawValue);
    if (!Number.isFinite(timeoutNum) || timeoutNum < 0) {
      return 0;
    }

    if (timeoutNum === 0) return 0;
    if (timeoutNum < 5) return 5;

    return timeoutNum;
  };

  const {
    enableExpiration,
    ConversationTimeout,
    ExpirationStepName,
    enableReactivation,
    reactivationTimeout,
    reactivationMessage,
    reactivationMaxMessages,
    reactivationInterval,
  } = formData as unknown as Record<string, unknown>;
  const isExpirationEnabled = Boolean(enableExpiration);
  const isReactivationEnabled = Boolean(enableReactivation);

  const workflowAny = originalWorkflow as any;
  
  const sanitized: any = {
    ...originalWorkflow,
  };

  delete sanitized.id;
  delete sanitized.rowDate;
  delete sanitized.workflowSteps;
  delete sanitized.enableExpiration;
  delete sanitized.enableReactivation;
  
  delete sanitized.ConversationTimeout;
  delete sanitized.conversationTimeout;
  delete sanitized.ExpirationStepName;
  delete sanitized.expirationStepName;
  
  delete sanitized.reactivationTimeout;
  delete sanitized.ReactivationTimeout;
  delete sanitized.reactivationMessage;
  delete sanitized.ReactivationMessage;
  delete sanitized.reactivationMaxMessages;
  delete sanitized.ReactivationMaxMessages;
  delete sanitized.reactivationInterval;
  delete sanitized.ReactivationInterval;

  delete sanitized.productSearchAssertiveness;
  delete sanitized.ProductSearchAssertiveness;
  delete sanitized.workflowKeyWords;
  delete sanitized.WorkflowKeyWords;
  delete sanitized.workflowKeyWordsRules;
  delete sanitized.workflowKeyWordDrafts;

  sanitized.name = originalWorkflow?.name || "";
  sanitized.displayName = originalWorkflow?.displayName || "";
  sanitized.description = originalWorkflow?.description || "";
  sanitized.active = originalWorkflow?.active ?? false;
  sanitized.workflowTypeId = originalWorkflow?.workflowTypeId || 1;

  // Handle expiration
  if (!isExpirationEnabled) {
    sanitized.conversationTimeout = 180;
    sanitized.expirationStepName = null;
  } else {
    sanitized.conversationTimeout = normalizeExpirationTimeout(ConversationTimeout);

    if (ExpirationStepName === "" || ExpirationStepName === undefined) {
      sanitized.expirationStepName = null;
    } else {
      sanitized.expirationStepName = ExpirationStepName;
    }
  }

  // Handle reactivation
  if (!isReactivationEnabled) {
    sanitized.reactivationTimeout = 0;
    sanitized.reactivationMessage = null;
    sanitized.reactivationMaxMessages = null;
    sanitized.reactivationInterval = null;
  } else {
    const rawTimeout = reactivationTimeout;
    if (rawTimeout === "" || rawTimeout === undefined || rawTimeout === null) {
      sanitized.reactivationTimeout = 0;
    } else {
      const timeoutNum = Number(rawTimeout);
      sanitized.reactivationTimeout = Number.isFinite(timeoutNum) ? Math.max(0, timeoutNum) : 0;
    }

    const rawMessage = reactivationMessage;
    if (rawMessage === "" || rawMessage === undefined) {
      sanitized.reactivationMessage = null;
    } else {
      sanitized.reactivationMessage = typeof rawMessage === "string" ? rawMessage.trim() : null;
    }

    const rawMaxMessages = reactivationMaxMessages;
    if (rawMaxMessages === "" || rawMaxMessages === undefined || rawMaxMessages === null) {
      sanitized.reactivationMaxMessages = null;
    } else {
      const maxMessagesNum = Number(rawMaxMessages);
      sanitized.reactivationMaxMessages = Number.isFinite(maxMessagesNum) ? Math.max(0, maxMessagesNum) : null;
    }

    const rawInterval = reactivationInterval;
    if (rawInterval === "" || rawInterval === undefined || rawInterval === null) {
      sanitized.reactivationInterval = null;
    } else {
      const intervalNum = Number(rawInterval);
      sanitized.reactivationInterval = Number.isFinite(intervalNum) ? Math.max(0, intervalNum) : null;
    }
  }

  const formRecord = formData as Record<string, unknown>;
  const rawDisplayType =
    formRecord.productDisplayType ?? formRecord.ProductDisplayType ?? originalWorkflow?.productDisplayType;
  const normalizedDisplayType = String(rawDisplayType || "LIST").toUpperCase();
  sanitized.productDisplayType = normalizedDisplayType === "CAROUSEL" ? "CAROUSEL" : "LIST";

  const rawOrderReportLayout =
    formRecord.orderReportLayout ?? formRecord.OrderReportLayout ?? originalWorkflow?.orderReportLayout;
  sanitized.orderReportLayout = String(rawOrderReportLayout ?? "").toUpperCase() === "R" ? "R" : "A";

  const rawAssertiveness =
    formRecord.productSearchAssertiveness ?? formRecord.ProductSearchAssertiveness;
  if (rawAssertiveness === "" || rawAssertiveness === undefined || rawAssertiveness === null) {
    sanitized.productSearchAssertiveness = 50;
  } else {
    const assertivenessNum = Number(rawAssertiveness);
    sanitized.productSearchAssertiveness = Number.isFinite(assertivenessNum)
      ? Math.min(100, Math.max(0, assertivenessNum))
      : 50;
  }

  const workflowKeyWordsRules = finalizeWorkflowKeyWordRules(
    formData.workflowKeyWordsRules || [],
    formData.workflowKeyWordDrafts || {}
  );
  try {
    sanitized.workflowKeyWords = serializeWorkflowKeyWords(workflowKeyWordsRules);
  } catch {
    sanitized.workflowKeyWords = readWorkflowKeyWords(originalWorkflow);
  }

  return sanitized;
};



